import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchCurrentBusiness, markOtcWalkthroughStepViewed } from "api/fetchers";
import { toast } from "react-toastify";

const VIEWPORT_GAP = 16;
const TARGET_GAP = 14;
const TARGET_PADDING = 6;
const CARD_WIDTH = 410;

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const getCardPosition = (target, placement, cardWidth, alignment = "center") => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const estimatedHeight = 220;
    const alignedTop = clamp(
        alignment === "start"
            ? target.top
            : alignment === "end"
            ? target.bottom - estimatedHeight
            : target.top + target.height / 2 - estimatedHeight / 2,
        VIEWPORT_GAP,
        Math.max(VIEWPORT_GAP, viewportHeight - estimatedHeight - VIEWPORT_GAP)
    );

    const positions = {
        left: {
            left: target.left - cardWidth - TARGET_GAP,
            top: alignedTop,
        },
        right: {
            left: target.right + TARGET_GAP,
            top: alignedTop,
        },
        top: {
            left: target.left + target.width / 2 - cardWidth / 2,
            top: target.top - estimatedHeight - TARGET_GAP,
        },
        bottom: {
            left: target.left + target.width / 2 - cardWidth / 2,
            top: target.bottom + TARGET_GAP,
        },
    };

    const fitsLeft = positions.left.left >= VIEWPORT_GAP;
    const fitsRight = positions.right.left + cardWidth <= viewportWidth - VIEWPORT_GAP;
    let resolvedPlacement = placement;

    if (placement === "left" && !fitsLeft) {
        resolvedPlacement = fitsRight ? "right" : "bottom";
    }
    if (placement === "right" && !fitsRight) {
        resolvedPlacement = fitsLeft ? "left" : "bottom";
    }
    let position = positions[resolvedPlacement] || positions.right;
    if (
        resolvedPlacement === "bottom" &&
        position.top + estimatedHeight > viewportHeight - VIEWPORT_GAP &&
        positions.top.top >= VIEWPORT_GAP
    ) {
        position = positions.top;
    }
    if (
        resolvedPlacement === "top" &&
        position.top < VIEWPORT_GAP &&
        positions.bottom.top + estimatedHeight <= viewportHeight - VIEWPORT_GAP
    ) {
        position = positions.bottom;
    }

    return {
        left: clamp(position.left, VIEWPORT_GAP, viewportWidth - cardWidth - VIEWPORT_GAP),
        top: clamp(position.top, VIEWPORT_GAP, viewportHeight - estimatedHeight - VIEWPORT_GAP),
    };
};

const getWalkthroughSteps = (currentBusiness) => {
    const candidates = [
        currentBusiness,
        currentBusiness?.business,
        currentBusiness?.data,
        currentBusiness?.data?.business,
    ];

    return candidates.find((candidate) => Array.isArray(candidate?.hasViewOtcWalkthrough))
        ?.hasViewOtcWalkthrough;
};

const markStepViewedInBusiness = (currentBusiness, step) => {
    if (!currentBusiness || typeof currentBusiness !== "object") return currentBusiness;

    if (Array.isArray(currentBusiness.hasViewOtcWalkthrough)) {
        return {
            ...currentBusiness,
            hasViewOtcWalkthrough: currentBusiness.hasViewOtcWalkthrough.map((item) =>
                item?.step === step ? { ...item, viewed: true } : item
            ),
        };
    }

    if (currentBusiness.data && typeof currentBusiness.data === "object") {
        return {
            ...currentBusiness,
            data: markStepViewedInBusiness(currentBusiness.data, step),
        };
    }

    if (currentBusiness.business && typeof currentBusiness.business === "object") {
        return {
            ...currentBusiness,
            business: markStepViewedInBusiness(currentBusiness.business, step),
        };
    }

    return currentBusiness;
};

export const useFeatureTour = (step, enabled = true) => {
    const queryClient = useQueryClient();
    const [isOpen, setIsOpen] = useState(false);
    const { data: currentBusiness, isLoading } = useQuery({
        queryKey: ["fetchCurrentBusiness"],
        queryFn: fetchCurrentBusiness,
    });
    const walkthroughSteps = getWalkthroughSteps(currentBusiness);
    const hasViewedStep = walkthroughSteps?.find((item) => item?.step === step)?.viewed;

    const { mutate: markAsViewed } = useMutation({
        mutationFn: () => markOtcWalkthroughStepViewed(step),
        onMutate: async () => {
            await queryClient.cancelQueries(["fetchCurrentBusiness"]);
            const previousBusiness = queryClient.getQueryData(["fetchCurrentBusiness"]);
            queryClient.setQueryData(["fetchCurrentBusiness"], (business) =>
                markStepViewedInBusiness(business, step)
            );
            return { previousBusiness };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousBusiness) {
                queryClient.setQueryData(["fetchCurrentBusiness"], context.previousBusiness);
            }
            setIsOpen(Boolean(enabled));
            toast.error("We couldn't save your walkthrough progress. Please try again.");
        },
        onSettled: () => {
            queryClient.invalidateQueries(["fetchCurrentBusiness"]);
        },
    });

    useEffect(() => {
        setIsOpen(
            Boolean(enabled) &&
                !isLoading &&
                Array.isArray(walkthroughSteps) &&
                hasViewedStep === false
        );
    }, [enabled, hasViewedStep, isLoading, walkthroughSteps]);

    const dismiss = () => {
        setIsOpen(false);
        if (hasViewedStep === false) markAsViewed();
    };

    return { isOpen, dismiss, setIsOpen };
};

const FeatureTourPopover = ({
    open,
    target,
    title,
    description,
    primaryLabel = "Got it",
    secondaryLabel,
    onPrimary,
    onSecondary,
    placement = "right",
    alignment = "center",
}) => {
    const [targetRect, setTargetRect] = useState(null);

    useLayoutEffect(() => {
        if (!open) {
            setTargetRect(null);
            return undefined;
        }

        const updatePosition = () => {
            const element = document.querySelector(target);
            if (!element) {
                setTargetRect(null);
                return;
            }
            const rect = element.getBoundingClientRect();
            setTargetRect({
                top: Math.max(0, rect.top - TARGET_PADDING),
                left: Math.max(0, rect.left - TARGET_PADDING),
                right: Math.min(window.innerWidth, rect.right + TARGET_PADDING),
                bottom: Math.min(window.innerHeight, rect.bottom + TARGET_PADDING),
                width: rect.width + TARGET_PADDING * 2,
                height: rect.height + TARGET_PADDING * 2,
            });
        };

        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        const observer =
            typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updatePosition);
        const element = document.querySelector(target);
        if (element) observer?.observe(element);

        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
            observer?.disconnect();
        };
    }, [open, target]);

    useEffect(() => {
        if (!open) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    if (!open || !targetRect) return null;

    const cardWidth = Math.min(CARD_WIDTH, window.innerWidth - VIEWPORT_GAP * 2);
    const cardPosition = getCardPosition(targetRect, placement, cardWidth, alignment);
    const backdropClass = "fixed bg-black/40 z-[9998]";

    return createPortal(
        <div role="dialog" aria-modal="true" aria-label={title}>
            <div
                className={backdropClass}
                style={{ inset: `0 0 auto 0`, height: targetRect.top }}
            />
            <div
                className={backdropClass}
                style={{
                    left: 0,
                    top: targetRect.top,
                    width: targetRect.left,
                    height: targetRect.height,
                }}
            />
            <div
                className={backdropClass}
                style={{
                    left: targetRect.right,
                    right: 0,
                    top: targetRect.top,
                    height: targetRect.height,
                }}
            />
            <div className={backdropClass} style={{ inset: `${targetRect.bottom}px 0 0 0` }} />

            <div
                aria-hidden="true"
                className="pointer-events-none fixed z-[9999] rounded-lg ring-4 ring-white shadow-[0_0_0_1px_rgba(111,0,255,0.08)]"
                style={{
                    left: targetRect.left,
                    top: targetRect.top,
                    width: targetRect.width,
                    height: targetRect.height,
                }}
            />

            <div
                className="fixed z-[10000] rounded-xl bg-white !p-5 shadow-[0_12px_32px_rgba(16,24,40,0.16)] sm:p-6"
                style={{ ...cardPosition, width: cardWidth }}
            >
                <h2 className="mb-0 text-xl font-semibold text-[#101828]">{title}</h2>
                <div className="mt-3 text-sm leading-6 text-[#344054]">{description}</div>
                <div className="mt-6 flex items-center justify-end gap-3">
                    {secondaryLabel && (
                        <button
                            type="button"
                            onClick={onSecondary}
                            className="mr-auto rounded-lg px-0 py-2 text-sm font-medium text-[#475467]"
                        >
                            {secondaryLabel}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onPrimary}
                        className="rounded-lg bg-[#6F00FF] px-6 py-2.5 text-sm font-medium text-white transition hover:bg-[#5A00D6]"
                    >
                        {primaryLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default FeatureTourPopover;
